import { memo, ReactNode } from "react";
import {
  ResponsiveBar,
  BarDatum,
  ComputedBarDatum,
  BarCustomLayerProps,
} from "@nivo/bar";
import { Margin, PropertyAccessor } from "@nivo/core";

import { EmptyChartCardComponent } from "@zenith/features/Analytics";
import { getSuffixedNumber, formatCurrency } from "@zenith/utils";
import { getChartTheme } from "@zenith/utils/Helpers";
import { DEFAULT_CURRENCY_SYMBOL } from "@zenith/data/constants";

/* eslint-disable-next-line */
export interface BarChartComponentProps {
  data: BarDatum[];
  indexBy: PropertyAccessor<BarDatum, string> | undefined;
  layout: "horizontal" | "vertical" | undefined;
  legend?: ReactNode;
  keys: string[] | undefined;
  margin?: Partial<Margin>;
  maxWidth?: string | number;
  height?: string | number;
  minWidth?: string | number;
  border?: boolean;
  width?: number | string;
  axisBottom?: object;
  formatAxisLabel?: (value: string) => string;
  getTooltipFullName?: (labelValue: string) => string;
}

const baseTheme = getChartTheme();
const theme = {
  ...baseTheme,
  axis: {
    ...baseTheme.axis,
    ticks: {
      ...(baseTheme.axis?.ticks || {}),
      text: {
        ...(baseTheme.axis?.ticks?.text || {}),
        fontSize: 12,
        fill: "#8F8F8F",
      },
    },
  },
  grid: {
    ...baseTheme.grid,
    line: {
      ...(baseTheme.grid?.line || {}),
      stroke: "#E5E5E5",
      strokeWidth: 1,
    },
  },
};

const roundToNiceNumber = (value: number): number => {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / Math.pow(10, exponent);

  let niceFraction;
  if (fraction <= 1.5) niceFraction = 1;
  else if (fraction <= 3) niceFraction = 2;
  else if (fraction <= 7) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * Math.pow(10, exponent);
};

const calculateNiceTicks = (
  maxValue: number,
  targetTickCount = 5
): number[] => {
  if (maxValue <= 0) {
    return [0];
  }
  const niceMax = roundToNiceNumber(maxValue);
  const tickSpacing = roundToNiceNumber(niceMax / targetTickCount);
  const ticks: number[] = [];
  let currentTick = 0;

  while (currentTick <= niceMax) {
    ticks.push(currentTick);
    currentTick += tickSpacing;
  }

  return ticks;
};

const HorizontalBarLabels = memo(function HorizontalBarLabels(
  barProps: BarCustomLayerProps<BarDatum>
) {
  const { bars } = barProps;

  return (
    <g>
      {bars.map((bar: ComputedBarDatum<BarDatum>) => {
        const { width, height, x, y, data } = bar;
        return (
          <text
            key={`${x}.${y}`}
            x={10 + width}
            y={y + 4 + height / 2}
            textAnchor="start"
            style={{
              fill: "#8F8F8F",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {data.value && data.value > 0 ? getSuffixedNumber(data.value) : ""}
          </text>
        );
      })}
    </g>
  );
});

const VerticalBarLabels = memo(function VerticalBarLabels(
  barProps: BarCustomLayerProps<BarDatum>
) {
  const { bars } = barProps;

  return (
    <g>
      {bars.map((bar: ComputedBarDatum<BarDatum>) => {
        const { width, x, y, data } = bar;
        return (
          <text
            key={`${x}.${y}`}
            x={x + width / 2}
            y={y - 10}
            textAnchor="middle"
            style={{
              fill: "#8F8F8F",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {data.value && data.value > 0 ? getSuffixedNumber(data.value) : ""}
          </text>
        );
      })}
    </g>
  );
});

const BarChartComponent = memo(function BarChartComponent(
  props: BarChartComponentProps
) {
  const {
    data,
    indexBy,
    keys,
    layout,
    legend,
    margin,
    border = true,
    height = 350,
    maxWidth = 650,
    width,
    minWidth = "full",
    formatAxisLabel,
    getTooltipFullName,
  } = props;

  const isHorizontal = layout === "horizontal";

  const maxValue = Math.max(...data.map((item) => Number(item.amount) || 0));
  const yAxisTicks = calculateNiceTicks(maxValue);
  const isEmptyValues = data.every((item) => item.amount === 0);

  return (
    <div
      className={`flex w-full rounded-lg p-8 ${
        border ? "border border-zinc-300" : ""
      }`}
    >
      {!data.length || isEmptyValues ? (
        <EmptyChartCardComponent />
      ) : (
        <div
          className="relative h-full w-full"
          style={{ maxWidth, minWidth, height, width }}
        >
          <ResponsiveBar
            data={data}
            indexBy={indexBy}
            keys={keys}
            enableLabel={false}
            margin={margin}
            borderRadius={10}
            maxValue={maxValue}
            axisTop={null}
            axisRight={null}
            colors={["#F9DCD9"]}
            theme={{
              ...theme,
              tooltip: {
                container: {
                  background: "#373737",
                  color: "white",
                  borderRadius: "8px",
                  fontSize: "12px",
                  padding: "8px 12px",
                },
              },
            }}
            padding={0.6}
            valueFormat={(value) => getSuffixedNumber(value)}
            axisLeft={
              isHorizontal
                ? {
                    tickSize: 0,
                    format: (value) => {
                      if (formatAxisLabel) {
                        return formatAxisLabel(value);
                      }
                      const fullName = value.split(" "),
                        firstName = fullName[0];
                      return firstName;
                    },
                  }
                : {
                    tickSize: 0,
                    tickPadding: 10,
                    ticksPosition: "before",
                    tickValues: yAxisTicks,
                    format: function (value) {
                      return getSuffixedNumber(value);
                    },
                  }
            }
            axisBottom={
              isHorizontal
                ? {
                    legend,
                    legendPosition: "middle",
                    tickSize: 0,
                    tickValues: 6,
                    format: (value) => {
                      return getSuffixedNumber(value);
                    },
                  }
                : {
                    legend,
                    legendOffset: 35,
                    legendPosition: "middle",
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: 45,
                    format: (value) => {
                      const maxLength = 7;
                      if (value.length > maxLength) {
                        return value.substring(0, maxLength) + "...";
                      }
                      return value;
                    },
                  }
            }
            layout={layout}
            enableGridX={false}
            enableGridY={true}
            gridYValues={yAxisTicks}
            markers={[
              {
                axis: isHorizontal ? "x" : "y",
                value: 0,
                lineStyle: {
                  stroke: "#E5E5E5",
                  strokeWidth: 1,
                },
              },
            ]}
            tooltip={(val) => {
              const labelValue = val.indexValue as string;
              const amount = val.data?.amount as number;
              const currentStock = val.data?.currentStock as number;
              const quantity = val.data?.quantity as number;
              const displayName = getTooltipFullName
                ? getTooltipFullName(labelValue)
                : labelValue;
              return (
                <div className="rounded-lg bg-neutral-800 px-3 py-2 text-white shadow-lg">
                  <p className="text-sm font-medium">
                    {currentStock > 0
                      ? `${displayName}: ${currentStock}`
                      : quantity > 0
                        ? `${displayName}: ${quantity}`
                        : `${displayName}: ${DEFAULT_CURRENCY_SYMBOL} ${formatCurrency(amount, false)}`}
                  </p>
                </div>
              );
            }}
            role="application"
            layers={[
              "grid",
              "axes",
              "bars",
              "markers",
              "legends",
              "annotations",
              isHorizontal ? HorizontalBarLabels : VerticalBarLabels,
            ]}
            isFocusable={false}
          />
        </div>
      )}
    </div>
  );
});

export default BarChartComponent;
